import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Activity, Plus, Move, UserPlus, MessageCircle, Trash2, CheckCircle2, Archive, Paperclip } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API } from "@/utils/constants";
import { Avatar, getShortName } from "@/components/shared";

const ACTION_META = {
  created: { icon: Plus, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10", verb: "created" },
  moved: { icon: Move, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10", verb: "moved" },
  assigned: { icon: UserPlus, color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-500/10", verb: "assigned" },
  comment_added: { icon: MessageCircle, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10", verb: "commented on" },
  attachment_added: { icon: Paperclip, color: "text-cyan-600 dark:text-cyan-400", bg: "bg-cyan-500/10", verb: "attached a file to" },
  deleted: { icon: Trash2, color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10", verb: "deleted" },
  bulk_complete: { icon: CheckCircle2, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10", verb: "completed" },
  bulk_archive: { icon: Archive, color: "text-slate-600 dark:text-slate-400", bg: "bg-slate-500/10", verb: "archived" },
  bulk_delete: { icon: Trash2, color: "text-red-600 dark:text-red-400", bg: "bg-red-500/10", verb: "bulk-deleted" },
  bulk_move: { icon: Move, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10", verb: "moved" },
};

const fallbackMeta = { icon: Activity, color: "text-muted-foreground", bg: "bg-muted", verb: "updated" };

const ActivityRow = ({ activity }) => {
  const meta = ACTION_META[activity.action] || fallbackMeta;
  const Icon = meta.icon;
  const rawWho = activity.user_name || "Someone";
  const who = getShortName(rawWho);
  const when = activity.created_at
    ? formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })
    : "";

  let detailText = null;
  if (activity.action === "moved" && activity.details?.from && activity.details?.to) {
    detailText = (
      <span className="text-xs text-muted-foreground">
        from <span className="font-medium text-foreground/70">{activity.details.from}</span>
        {" → "}
        <span className="font-medium text-foreground/70">{activity.details.to}</span>
      </span>
    );
  } else if (activity.action === "assigned" && activity.details?.assigned_ids?.length) {
    detailText = (
      <span className="text-xs text-muted-foreground">
        {activity.details.assigned_ids.length} {activity.details.assigned_ids.length === 1 ? "person" : "people"}
      </span>
    );
  } else if (activity.action === "attachment_added" && activity.details?.filename) {
    detailText = (
      <span className="text-xs text-muted-foreground truncate max-w-[200px]">
        {activity.details.filename}
      </span>
    );
  }

  return (
    <div className="flex items-start gap-2.5 py-2.5 px-4 hover:bg-muted/30 transition-colors" data-testid={`activity-row-${activity.id}`}>
      <div className="relative flex-shrink-0 mt-0.5">
        <Avatar name={rawWho} size="md" />
        <div className={`${meta.bg} ${meta.color} absolute -bottom-1 -right-1 h-4 w-4 rounded-full flex items-center justify-center border border-card`}>
          <Icon className="h-2.5 w-2.5" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm leading-snug">
          <span className="font-semibold" title={rawWho}>{who}</span>{" "}
          <span className="text-muted-foreground">{meta.verb}</span>{" "}
          <span className="font-medium truncate">{activity.entity_title}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {detailText}
          <span className="text-[11px] text-muted-foreground/70">{when}</span>
        </div>
      </div>
    </div>
  );
};

export const ActivityFeed = ({ open, onOpenChange, activeWorkspaceId, refreshTrigger }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/activity`, { params: { limit: 50 } });
      setActivities(r.data || []);
    } catch (e) {
      console.error("Failed to fetch activity", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetch();
  }, [open, fetch, activeWorkspaceId, refreshTrigger]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[360px] sm:w-[420px] p-0 flex flex-col" data-testid="activity-feed-panel">
        <SheetHeader className="px-4 py-3.5 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-primary" />Recent activity
            {activities.length > 0 && <span className="text-xs font-normal text-muted-foreground">({activities.length})</span>}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto py-1">
          {loading && activities.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Loading…</p>
          ) : activities.length === 0 ? (
            <div className="text-center py-14 text-muted-foreground">
              <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No activity yet</p>
            </div>
          ) : (
            activities.map(a => <ActivityRow key={a.id} activity={a} />)
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
